import React, { useRef, useEffect, useState } from 'react';
import EditorPage from './EditorPage';
import { FiChevronLeft, FiX, FiCheck } from 'react-icons/fi';
import { useNavigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { clearCurrentTemplateId } from '../../features/templates/templatesSlice';
import { toast } from 'react-toastify';

const FiChevronLeftComponent = FiChevronLeft as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiXComponent = FiX as any;
const FiCheckComponent = FiCheck as any;

const CreateTemplatePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // Get the current template ID from localStorage
  const templateId = localStorage.getItem('currentTemplateId');
  const isEditMode = !!templateId;

  const fromTab2 = (location.state as { fromTab2?: string })?.fromTab2;

  const editorRef = useRef<{
    handleSave: () => void;
    handlePreview: () => void;
  }>(null);

  // State for tags on the create page (ONLY for new templates)
  const [tags, setTags] = useState<string[]>([]);
  const [newTagValue, setNewTagValue] = useState<string>('');
  const [isAddingTag, setIsAddingTag] = useState<boolean>(false);
  const [isUpdatingTags, setIsUpdatingTags] = useState<boolean>(false);

  // Load existing tags from localStorage only when creating new template
  useEffect(() => {
    // Only load tags if it's a new template (not edit mode)
    if (!isEditMode) {
      const savedTags = localStorage.getItem('newTemplateTags');
      if (savedTags) {
        try {
          const parsedTags = JSON.parse(savedTags);
          setTags(parsedTags);
        } catch (e) {
          // console.error('Failed to parse saved tags:', e);
          toast.error('Failed to load saved tags');
          setTags([]);
        }
      } else {
        setTags([]);
      }
    }
  }, [isEditMode]);

  // Save tags to localStorage whenever they change (only for new templates)
  useEffect(() => {
    if (!isEditMode) {
      localStorage.setItem('newTemplateTags', JSON.stringify(tags));
    }
  }, [tags, isEditMode]);

  const handleSave = () => {
    if (editorRef.current) {
      editorRef.current.handleSave();
    }
  };

  const handlePreview = () => {
    if (editorRef.current) {
      editorRef.current.handlePreview();
    }
  };

  const handleBack = () => {
    const templateId = localStorage.getItem('currentTemplateId');
    localStorage.removeItem('currentTemplateId');
    // Clear tags when going back
    if (!isEditMode) {
      localStorage.removeItem('newTemplateTags');
    }
    dispatch(clearCurrentTemplateId());

    if (templateId) {
      localStorage.setItem('scrollToTemplateId', templateId);
    }
    navigate(`/templates?tab=${fromTab2}`, {
      state: { refresh: true },
    });
  };

  // Tag handlers
  const handleAddTag = () => {
    if (isAddingTag) return;
    setIsAddingTag(true);
    setNewTagValue('');
  };

  const handleConfirmAddTag = () => {
    const trimmedTag = newTagValue.trim();
    if (!trimmedTag) {
      setIsAddingTag(false);
      setNewTagValue('');
      return;
    }

    // Check if tag already exists
    if (tags.includes(trimmedTag)) {
      toast.warning('Tag already exists');
      return;
    }

    setTags([...tags, trimmedTag]);
    setIsAddingTag(false);
    setNewTagValue('');
    // toast.success('Tag added successfully!');
  };

  const handleCancelAddTag = () => {
    setIsAddingTag(false);
    setNewTagValue('');
  };

  const handleRemoveTag = (index: number) => {
    const updatedTags = tags.filter((_: string, i: number) => i !== index);
    setTags(updatedTags);
    // toast.success('Tag removed successfully!');
  };

  return (
    <div className='p-2'>
      <button
        onClick={handleBack}
        className='flex items-center text-sm text-teal-600 mb-4 hover:underline font-semibold'
        style={{ color: '#00899E', fontSize: '14px' }}
      >
        <FiChevronLeftComponent className='mr-1' /> Back to Templates
      </button>

      <div className='flex items-center justify-between mb-4'>
        <h1 className='text-[20px] font-bold text-[#3F5265]'>
          {isEditMode ? 'Edit Template' : 'Create Template'}
        </h1>
        <div className='flex gap-2'>
          <button
            onClick={handlePreview}
            className='px-4 py-2 rounded border border-orange-500 text-orange-500 bg-white hover:bg-orange-50 text-sm'
          >
            Preview
          </button>
          <button
            onClick={handleSave}
            className='px-4 py-2 bg-orange-500 text-white rounded text-sm hover:bg-orange-600 transition'
          >
            {isEditMode && fromTab2 !== 'templatesGallery'
              ? 'Save Template'
              : isEditMode && fromTab2 === 'templatesGallery'
                ? 'Save a Copy'
                : 'Save Template'}
          </button>
        </div>
      </div>

      {/* Tags Section for Create/Edit Template */}
      {!isEditMode && (
        <div className='mb-4'>
          <div className='flex flex-wrap gap-3 items-center'>

            {/* Show existing tags */}
            {tags.map((tag: string, index: number) => (
              <span
                key={index}
                className='pl-[11px] pr-[8px] py-[8px] bg-[#ddfafb] text-[#01a3c0] gap-2 rounded-full text-xs font-medium shadow-md flex items-center'
              >
                {tag}
                <span
                  className='cursor-pointer hover:text-red-500 transition-colors'
                  onClick={() => handleRemoveTag(index)}
                >
                  <FiXComponent size={16} />
                </span>
              </span>
            ))}

            {/* Show add tag button or input field */}
            {isAddingTag ? (
              <div className='flex items-center gap-1 bg-white border border-teal-300 rounded-full px-2 py-1 shadow-sm'>
                <input
                  type='text'
                  value={newTagValue}
                  onChange={(e) => setNewTagValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleConfirmAddTag();
                    } else if (e.key === 'Escape') {
                      handleCancelAddTag();
                    }
                  }}
                  placeholder='Enter tag...'
                  className='text-xs w-24 focus:outline-none px-1 py-1'
                  autoFocus
                  disabled={isUpdatingTags}
                />
                <button
                  onClick={handleConfirmAddTag}
                  disabled={isUpdatingTags || !newTagValue.trim()}
                  className='p-1 text-green-600 hover:bg-green-50 rounded transition-colors disabled:opacity-50'
                  title='Add tag'
                >
                  <FiCheckComponent size={14} />
                </button>
                <button
                  onClick={handleCancelAddTag}
                  disabled={isUpdatingTags}
                  className='p-1 text-red-500 hover:bg-red-50 rounded transition-colors'
                  title='Cancel'
                >
                  <FiXComponent size={14} />
                </button>
              </div>
            ) : (
              <span
                className='px-3 py-[8px] bg-[#01a3c0] text-white rounded-full text-xs font-medium cursor-pointer shadow-md hover:bg-cyan-600 transition-colors'
                onClick={handleAddTag}
              >
                Add tag
              </span>
            )}
          </div>
        </div>
      )}

      <EditorPage ref={editorRef} fromTab2={fromTab2} />
      <ToastContainer position='top-right' autoClose={3000} />
    </div>
  );
};

export default CreateTemplatePage;